// src/belediye/components/steps/citizenDelivery/CitizenDocumentsPanel.js
//
// Vatandaşa belge teslimi paneli — FINAL adımının içinde.
// - Küçük kart ızgarası; resim → thumbnail önizleme, diğer → ikon.
// - Karta tıkla → modal önizleme. Sil/Gizle → ONAY + BİLGİ modalı.
// - Yükleme/iletim yalnız kapanmadan önce. Yetki backend'de de enforce edilir.
// - Medya URL'i backend origin ile mutlaklaştırılır (göreli /media/ SPA origin'inde çözülmez).

import React, { useCallback, useEffect, useState } from "react";
import {
  DocumentArrowUpIcon,
  PaperAirplaneIcon,
  EyeSlashIcon,
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import BasicModal from "../../ui/BasicModal";
import {
  fetchShareableFiles,
  fetchSharedDocuments,
  forwardSharedDocument,
  uploadSharedDocument,
  removeSharedDocument,
  setSharedDocumentVisibility,
} from "../../../services/citizenDeliveryApi";

import "./CitizenDocumentsPanel.css";

const CLOSED_STATUSES = new Set(["çözüldü", "çözülemedi"]);
const isImage = (ct) => String(ct || "").toLowerCase().startsWith("image/");
const isPdf = (ct) => String(ct || "").toLowerCase() === "application/pdf";
const shortName = (s) => {
  const t = String(s || "").trim() || "Belge";
  return t.length > 16 ? `${t.slice(0, 14)}…` : t;
};

// Medya URL'ini backend origin'e göre mutlaklaştır (göreli /media/ SPA origin'inde 404 olur).
const MEDIA_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api").replace(/\/api\/?$/, "");
const mediaUrl = (u) => {
  if (!u) return u;
  return /^https?:\/\//i.test(u) ? u : `${MEDIA_BASE}${u}`;
};

export default function CitizenDocumentsPanel({ applicationId, applicationStatus }) {
  const [available, setAvailable] = useState(true);
  const [docs, setDocs] = useState([]);
  const [shareable, setShareable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("upload");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }
  const [perm, setPerm] = useState({}); // { can_send, can_delete, can_hide } — backend'den

  const closed = CLOSED_STATUSES.has(String(applicationStatus || "").trim());

  // Yetkiler TEK OTORİTE olarak backend'den gelir (rol tahmini yapılmaz → tutarlı).
  const canSend = !!perm.can_send;
  const canDelete = !!perm.can_delete;
  const canHide = !!perm.can_hide;

  const load = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError("");
    try {
      const [dRes, sRes] = await Promise.all([
        fetchSharedDocuments(applicationId),
        fetchShareableFiles(applicationId),
      ]);
      const dData = dRes?.data || {};
      if (Array.isArray(dData)) {
        setDocs(dData);
        setPerm({});
      } else {
        setDocs(Array.isArray(dData.items) ? dData.items : []);
        setPerm(dData.permissions || {});
      }
      setShareable(Array.isArray(sRes?.data) ? sRes.data : []);
      setAvailable(true);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 || st === 404) setAvailable(false);
      else setError("Belgeler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!available) return null;

  const handleForward = async (fileAssetId) => {
    setBusyId(`fa-${fileAssetId}`);
    setError("");
    try {
      await forwardSharedDocument(applicationId, { fileAssetId });
      await load();
    } catch {
      setError("İletim başarısız oldu.");
    } finally {
      setBusyId(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      await uploadSharedDocument(applicationId, { file, title: title.trim() });
      setFile(null);
      setTitle("");
      await load();
    } catch {
      setError("Yükleme başarısız oldu.");
    } finally {
      setUploading(false);
    }
  };

  const handleToggleVisibility = async (d) => {
    setBusyId(`doc-${d.id}`);
    setError("");
    try {
      await setSharedDocumentVisibility(d.id, !d.is_visible_to_citizen);
      await load();
    } catch {
      setError("İşlem başarısız oldu.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (id) => {
    setBusyId(`doc-${id}`);
    setError("");
    try {
      await removeSharedDocument(id);
      await load();
    } catch {
      setError("Kaldırma başarısız oldu.");
    } finally {
      setBusyId(null);
    }
  };

  // Onay + bilgi modalları
  const askDelete = (d) =>
    setConfirm({
      title: "Belgeyi sil",
      message:
        "Bu belgeyi silmek istediğinize emin misiniz? Belge vatandaş görünümünden kaldırılır ve geri getirilemez.",
      onConfirm: () => handleRemove(d.id),
    });
  const askToggleVisibility = (d) =>
    setConfirm({
      title: d.is_visible_to_citizen ? "Vatandaştan gizle" : "Vatandaşa göster",
      message: d.is_visible_to_citizen
        ? "Bu belge vatandaşın takip sayfasında GÖRÜNMEYECEK. Dilerseniz daha sonra tekrar gösterebilirsiniz."
        : "Bu belge vatandaşın takip sayfasında TEKRAR GÖRÜNECEK.",
      onConfirm: () => handleToggleVisibility(d),
    });

  const openPreview = (d) =>
    setPreview({
      url: mediaUrl(d.file_url),
      name: d.title || d.file_name || "Belge",
      content_type: d.content_type,
    });

  const statusChip = (d) => {
    if (!closed) return <span className="cdp-chip cdp-chip--pending">Bekliyor</span>;
    if (!d.is_visible_to_citizen) return <span className="cdp-chip cdp-chip--hidden">Gizli</span>;
    return <span className="cdp-chip cdp-chip--sent">İletildi</span>;
  };

  return (
    <div className="cdp-root" translate="no">
      <div className="cdp-title">
        <DocumentTextIcon className="cdp-icon" aria-hidden="true" />
        Vatandaşa Belge Teslimi
      </div>
      <div className="cdp-subtitle">
        {closed
          ? "Bu belgeler vatandaşın takip sayfasında görünür."
          : "Belgeler hazırlanır; başvuru kapatılınca vatandaşa iletilir."}
      </div>

      {canSend ? (
        <>
          <div className="cdp-tabs" role="tablist">
            <button
              type="button"
              className={`cdp-tab${tab === "upload" ? " is-active" : ""}`}
              onClick={() => setTab("upload")}
            >
              <DocumentArrowUpIcon className="cdp-tab-ic" />
              Yükle
            </button>
            <button
              type="button"
              className={`cdp-tab${tab === "forward" ? " is-active" : ""}`}
              onClick={() => setTab("forward")}
            >
              <PaperAirplaneIcon className="cdp-tab-ic" />
              Saha Dosyaları{shareable.length ? ` (${shareable.length})` : ""}
            </button>
          </div>

          {tab === "upload" ? (
            <>
              <div className="cdp-upload">
                <input
                  type="file"
                  className="cdp-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <input
                  type="text"
                  className="cdp-input"
                  placeholder="Başlık (opsiyonel)"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <button
                  type="button"
                  className={`aap-btn aap-btn-primary cdp-round${file ? " cdp-pulse" : ""}`}
                  disabled={!file || uploading}
                  onClick={handleUpload}
                >
                  {uploading ? "Yükleniyor…" : "Ekle"}
                </button>
              </div>
              {file ? (
                <div className="cdp-file-hint">
                  Seçildi: <b>{file.name}</b> — eklemek için <b>“Ekle”</b>ye basın.
                </div>
              ) : null}
            </>
          ) : shareable.length ? (
            <div className="cdp-grid">
              {shareable.map((f) => (
                <div className="cdp-card" key={`fa-${f.file_asset_id}`}>
                  <button
                    type="button"
                    className="cdp-card-main"
                    title={f.file_name || "Dosya"}
                    onClick={() =>
                      openPreview({ file_url: f.file_url, title: f.file_name, content_type: f.content_type })
                    }
                  >
                    {isImage(f.content_type) && f.file_url ? (
                      <img className="cdp-thumb" src={mediaUrl(f.file_url)} alt="" />
                    ) : (
                      <span className="cdp-thumb cdp-thumb--doc">
                        <DocumentTextIcon />
                      </span>
                    )}
                    <span className="cdp-card-name">{shortName(f.file_name)}</span>
                  </button>
                  <button
                    type="button"
                    className="cdp-card-fwd"
                    disabled={f.already_shared || busyId === `fa-${f.file_asset_id}`}
                    onClick={() => handleForward(f.file_asset_id)}
                  >
                    {f.already_shared ? "İletildi" : "İlet"}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="cdp-muted">İletilebilecek saha dosyası yok.</div>
          )}
        </>
      ) : null}

      <div className="cdp-section">
        <div className="cdp-section-label">Belgeler</div>
        {loading && !docs.length ? (
          <div className="cdp-muted">Yükleniyor…</div>
        ) : docs.length ? (
          <div className="cdp-grid">
            {docs.map((d) => (
              <div className="cdp-card" key={`doc-${d.id}`}>
                <button
                  type="button"
                  className="cdp-card-main"
                  title={d.title || d.file_name}
                  onClick={() => openPreview(d)}
                >
                  {isImage(d.content_type) && d.file_url ? (
                    <img className="cdp-thumb" src={mediaUrl(d.file_url)} alt="" />
                  ) : (
                    <span className="cdp-thumb cdp-thumb--doc">
                      <DocumentTextIcon />
                    </span>
                  )}
                  <span className="cdp-card-name">{shortName(d.title || d.file_name)}</span>
                </button>

                <div className="cdp-card-foot">{statusChip(d)}</div>

                {(canHide || canDelete) && (
                  <div className="cdp-card-actions">
                    {canHide && (
                      <button
                        type="button"
                        className="cdp-act"
                        title={d.is_visible_to_citizen ? "Vatandaştan gizle" : "Vatandaşa göster"}
                        disabled={busyId === `doc-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          askToggleVisibility(d);
                        }}
                      >
                        {d.is_visible_to_citizen ? (
                          <EyeSlashIcon className="cdp-act-ic" />
                        ) : (
                          <EyeIcon className="cdp-act-ic" />
                        )}
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="cdp-act cdp-act--danger"
                        title="Sil"
                        disabled={busyId === `doc-${d.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          askDelete(d);
                        }}
                      >
                        <TrashIcon className="cdp-act-ic" />
                      </button>
                    )}
                  </div>
                )}

                {d.created_by_name ? (
                  <div className="cdp-card-by" title={`Yükleyen: ${d.created_by_name}`}>
                    {d.created_by_name}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="cdp-muted">Henüz belge eklenmemiş.</div>
        )}
      </div>

      {error ? <div className="cdp-error">{error}</div> : null}

      {/* Önizleme modalı */}
      <BasicModal
        open={!!preview}
        title={preview?.name || "Önizleme"}
        onClose={() => setPreview(null)}
        size="lg"
      >
        {preview ? (
          isImage(preview.content_type) ? (
            <img src={preview.url} alt={preview.name} style={{ maxWidth: "100%", display: "block", margin: "0 auto" }} />
          ) : isPdf(preview.content_type) ? (
            <iframe title="Önizleme" src={preview.url} style={{ width: "100%", height: "70vh", border: 0 }} />
          ) : (
            <div className="cdp-muted">
              Bu dosya türü önizlenemiyor.{" "}
              <a href={preview.url} target="_blank" rel="noopener noreferrer">
                İndir
              </a>
            </div>
          )
        ) : null}
      </BasicModal>

      {/* Onay + bilgi modalı (sil / gizle / göster) */}
      <BasicModal
        open={!!confirm}
        title={confirm?.title || "Onay"}
        onClose={() => setConfirm(null)}
        size="sm"
        actions={
          <>
            <button type="button" className="aap-btn aap-btn-ghost" onClick={() => setConfirm(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="aap-btn aap-btn-primary"
              onClick={() => {
                const fn = confirm?.onConfirm;
                setConfirm(null);
                if (fn) fn();
              }}
            >
              Onayla
            </button>
          </>
        }
      >
        <div className="cdp-confirm-text">{confirm?.message}</div>
      </BasicModal>
    </div>
  );
}
